import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  defaultHeaders,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { ReceiptBatchState } from '@pflegenavi/service-provider-shared/api-endpoints';
import { SubmitReceiptBatchError } from '@pflegenavi/service-provider-shared/api-endpoints';

interface SubmitReceiptBatchServiceProviderInfoIncompleteException
  extends Error {
  errorCode: SubmitReceiptBatchError.SERVICE_PROVIDER_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchServiceProviderInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchServiceProviderInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.SERVICE_PROVIDER_INFO_INCOMPLETE
  );
};

interface SubmitReceiptBatchServiceProviderUserIncompleteException
  extends Error {
  errorCode: SubmitReceiptBatchError.SERVICE_PROVIDER_USER_INCOMPLETE;
}

export const isSubmitReceiptBatchServiceProviderUserIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchServiceProviderUserIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.SERVICE_PROVIDER_USER_INCOMPLETE
  );
};

interface SubmitReceiptBatchNursingHomeInfoIncompleteException extends Error {
  errorCode: SubmitReceiptBatchError.NURSING_HOME_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchNursingHomeInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchNursingHomeInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.NURSING_HOME_INFO_INCOMPLETE
  );
};

interface SubmitReceiptBatchResidentInfoIncompleteException extends Error {
  errorCode: SubmitReceiptBatchError.RESIDENT_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchResidentInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchResidentInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.RESIDENT_INFO_INCOMPLETE
  );
};

type State =
  | 'draft'
  | 'in_review'
  | 'change_requested'
  | 'declined'
  | 'accepted';
export interface GetReceiptBatchListParams {
  page: number;
  page_size: number;
  order_by?: Array<'updated_on'>;
  state?: ReceiptBatchState[];
  receipt_date?: {
    from?: Date;
    to?: Date;
  };
  accepted_date?: {
    from?: Date;
    to?: Date;
  };
}

interface PaginatedMeta {
  total_count: number;
}

export interface ReceiptBatchEntry {
  id: string;
  resident: {
    id: string;
    firstname: string;
    lastname: string;
    gender: string;
  };
  receipt_images: Array<{
    id: string;
    auto_generated: boolean;
  }>;
  receipt_date?: Date;
  line_items: Array<{
    id: string;
    amount_per_item: number;
    name: string;
    taxes: Array<{
      id: string;
      display_explanation: boolean;
      explanation_short?: string;
      explanation_long?: string;
      tax_abbreviation: string;
      tax_amount: string;
      tax_name: string;
      country_id: string;
    }>;
  }>;
}

export interface ReceiptBatch {
  id: string;
  created_on: Date;
  updated_on: Date;
  nursing_home: {
    id: string;
    name: string;
    tenant: string;
  };
  receipt_date?: Date;
  receipt_image_ids?: string[];
  state: State;
  accepted_on?: Date;
  generate_receipts: boolean;
  receipt_batch_entries: ReceiptBatchEntry[];
  use_individual_dates: boolean;
  // TODO:
  // - invalid_entry_count
  // - payment
}

export interface UpdateReceiptBatchEntry {
  id?: string;
  resident_id?: string | undefined;
  receipt_images?: Array<{
    id: string;
    auto_generated?: boolean;
  }>;
  receipt_date?: Date;
  line_items?: Array<{
    id?: string;
    amount_per_item?: number;
    name?: string;
    taxes?: Array<{
      id?: string;
      country_id: string;
      display_explanation: boolean;
      explanation_short?: string;
      explanation_long?: string;
      tax_abbreviation?: string;
      tax_amount: number;
      tax_name: string;
    }>;
  }>;
}
export interface UpdateReceiptBatch {
  id?: string;
  nursing_home_id?: string;
  receipt_date?: Date;
  receipt_image_ids?: string[];
  accepted_on?: Date;
  generate_receipts?: boolean;
  receipt_batch_entries?: UpdateReceiptBatchEntry[];
  use_individual_dates?: boolean;
}

export interface GetReceiptBatchListResult {
  data: ReceiptBatch[];
  meta: PaginatedMeta;
}

export interface ReceiptBatchResult {
  data: ReceiptBatch;
}

export interface IReceiptBatchPhoenixApi extends Api {
  getReceiptBatchList(dataIn: {
    params: GetReceiptBatchListParams;
  }): Promise<GetReceiptBatchListResult>;

  getReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchResult>;

  postReceiptBatch(dataIn: {
    body: UpdateReceiptBatch;
  }): Promise<ReceiptBatchResult>;

  patchReceiptBatch(dataIn: {
    params: { batchId: string };
    body: UpdateReceiptBatch;
  }): Promise<ReceiptBatchResult>;

  postReceiptBatchEntry(dataIn: {
    params: { batchId: string };
    body: { data: UpdateReceiptBatchEntry[] };
  }): Promise<ReceiptBatchResult>;

  patchReceiptBatchEntry(dataIn: {
    params: { batchId: string; entryId: string };
    body: UpdateReceiptBatchEntry;
  }): Promise<ReceiptBatchResult>;

  deleteReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<undefined>;

  deleteReceiptBatchEntry(dataIn: {
    params: {
      batchId: string;
      entryId: string;
    };
  }): Promise<ReceiptBatchResult>;

  submitReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<{ status: 'OK' }>;

  retractReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<{ status: 'OK' }>;

  postReceiptBatchPreview(dataIn: {
    params: { batchId: string };
  }): Promise<Blob>;

  postReceiptBatchEntryPreview(dataIn: {
    params: { batchId: string; entryId: string };
  }): Promise<Blob>;
}

export class ReceiptBatchPhoenixApi implements IReceiptBatchPhoenixApi {
  baseUrl: string;

  getReceiptBatchList: IReceiptBatchPhoenixApi['getReceiptBatchList'];
  getReceiptBatch: IReceiptBatchPhoenixApi['getReceiptBatch'];
  postReceiptBatch: IReceiptBatchPhoenixApi['postReceiptBatch'];
  patchReceiptBatch: IReceiptBatchPhoenixApi['patchReceiptBatch'];
  postReceiptBatchEntry: IReceiptBatchPhoenixApi['postReceiptBatchEntry'];
  patchReceiptBatchEntry: IReceiptBatchPhoenixApi['patchReceiptBatchEntry'];
  deleteReceiptBatch: IReceiptBatchPhoenixApi['deleteReceiptBatch'];
  deleteReceiptBatchEntry: IReceiptBatchPhoenixApi['deleteReceiptBatchEntry'];
  submitReceiptBatch: IReceiptBatchPhoenixApi['submitReceiptBatch'];
  retractReceiptBatch: IReceiptBatchPhoenixApi['retractReceiptBatch'];
  postReceiptBatchPreview: IReceiptBatchPhoenixApi['postReceiptBatchPreview'];
  postReceiptBatchEntryPreview: IReceiptBatchPhoenixApi['postReceiptBatchEntryPreview'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);

    this.postReceiptBatchPreview = async (dataIn) => {
      const finalUrl = `${this.baseUrl}/receipt-batch/${dataIn.params.batchId}/preview`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };

    this.postReceiptBatchEntryPreview = async (dataIn) => {
      const finalUrl = `${this.baseUrl}/receipt-batch/${dataIn.params.batchId}/entry/${dataIn.params.entryId}/preview`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };

    this.getReceiptBatchList = get({
      authContext,

      // eslint-disable-next-line complexity
      url: (opts) => {
        const query = new URLSearchParams();

        query.append('page', opts.page.toString());
        query.append('page_size', opts.page_size.toString());

        if (opts.order_by) {
          opts.order_by.map((orderBy) => {
            query.append('order_by[]', orderBy);
            query.append('order_directions[]', 'desc');
          });
        }

        let num_filters = 0;

        if (opts.state) {
          query.append(`filters[${num_filters}][field]`, 'state');
          query.append(`filters[${num_filters}][op]`, 'in');

          opts.state.map((state) => {
            query.append(`filters[${num_filters}][value][]`, state);
          });

          num_filters++;
        }

        if (opts.receipt_date?.from) {
          query.append(`receipt_date[${num_filters}][field]`, 'receipt_date');
          query.append(`receipt_date[${num_filters}][op]`, '>=');
          query.append(
            `receipt_date[${num_filters}][value]`,
            opts.receipt_date.from.toISOString()
          );

          num_filters++;
        }

        if (opts.receipt_date?.to) {
          query.append(`receipt_date[${num_filters}][field]`, 'receipt_date');
          query.append(`receipt_date[${num_filters}][op]`, '<=');
          query.append(
            `receipt_date[${num_filters}][value]`,
            opts.receipt_date.to.toISOString()
          );

          num_filters++;
        }

        if (opts.accepted_date?.from) {
          query.append(`receipt_date[${num_filters}][field]`, 'accepted_on');
          query.append(`receipt_date[${num_filters}][op]`, '>=');
          query.append(
            `receipt_date[${num_filters}][value]`,
            opts.accepted_date.from.toISOString()
          );

          num_filters++;
        }

        if (opts.accepted_date?.to) {
          query.append(`receipt_date[${num_filters}][field]`, 'accepted_on');
          query.append(`receipt_date[${num_filters}][op]`, '<=');
          query.append(
            `receipt_date[${num_filters}][value]`,
            opts.accepted_date.to.toISOString()
          );

          num_filters++;
        }

        const queryString = query.toString();
        return `${this.baseUrl}/receipt-batch?${queryString}`;
      },
    });

    this.getReceiptBatch = get({
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}`,
    });

    this.postReceiptBatch = modify({
      method: 'POST',
      authContext,
      url: `${this.baseUrl}/receipt-batch`,
    });

    this.patchReceiptBatch = modify({
      method: 'PATCH',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}`,
    });

    this.postReceiptBatchEntry = modify({
      method: 'POST',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}/entry`,
    });

    this.patchReceiptBatchEntry = modify({
      method: 'PATCH',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/entry/${params.entryId}`,
    });

    this.deleteReceiptBatch = modify({
      method: 'DELETE',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}`,
    });

    this.deleteReceiptBatchEntry = modify({
      method: 'DELETE',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/entry/${params.entryId}`,
    });

    this.submitReceiptBatch = modify({
      method: 'POST',
      authContext,
      url: (params) => `${this.baseUrl}/receipt-batch/${params.batchId}/submit`,
    });

    this.retractReceiptBatch = modify({
      method: 'POST',
      authContext,
      url: (params) =>
        `${this.baseUrl}/receipt-batch/${params.batchId}/retract`,
    });
  }
}
